import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const slidesData = {
  fr: [
    { id: 1, title: 'Haïti Verdoyant', subtitle: 'Préservons nos espaces naturels', alt: 'Paysage verdoyant d\'Haïti avec montagnes' },
    { id: 2, title: 'Rêves de Famille', subtitle: 'Construire un avenir meilleur ensemble', alt: 'Famille devant leur nouvelle maison moderne' },
    { id: 3, title: 'Fierté Haïtienne', subtitle: 'L\'Union Fait La Force', alt: 'Drapeau haïtien flottant fièrement' },
    { id: 4, title: 'Éducation & Formation', subtitle: 'Investir dans la jeunesse haïtienne', alt: 'Groupe de jeunes étudiants souriants' },
    { id: 5, title: 'Agriculture Durable', subtitle: 'L\'énergie solaire au service de nos terres', alt: 'Ferme solaire avec agriculteurs haïtiens' }
  ],
  en: [
    { id: 1, title: 'Green Haiti', subtitle: 'Let\'s preserve our natural spaces', alt: 'Green landscape of Haiti with mountains' },
    { id: 2, title: 'Family Dreams', subtitle: 'Building a better future together', alt: 'Family in front of their new modern home' },
    { id: 3, title: 'Haitian Pride', subtitle: 'Unity Makes Strength', alt: 'Haitian flag flying proudly' },
    { id: 4, title: 'Education & Training', subtitle: 'Investing in Haitian youth', alt: 'Group of smiling young students' },
    { id: 5, title: 'Sustainable Agriculture', subtitle: 'Solar energy serving our lands', alt: 'Solar farm with Haitian farmers' }
  ],
  ht: [
    { id: 1, title: 'Ayiti Vèt', subtitle: 'Ann konsève espas natirèl nou yo', alt: 'Peyzaj vèt Ayiti ak mòn' },
    { id: 2, title: 'Rèv Fanmi', subtitle: 'Bati yon avni pi bon ansanm', alt: 'Fanmi devan nouvo kay modèn yo' },
    { id: 3, title: 'Fyète Ayisyen', subtitle: 'L\'Union Fait La Force', alt: 'Drapo ayisyen k ap flote fyèman' },
    { id: 4, title: 'Edikasyon & Fòmasyon', subtitle: 'Envesti nan jèn Ayisyen yo', alt: 'Gwoup jèn elèv k ap souri' },
    { id: 5, title: 'Agrikilti Dirab', subtitle: 'Enèji solè nan sèvis tè nou yo', alt: 'Fèm solè ak kiltivatè Ayisyen' }
  ],
  es: [
    { id: 1, title: 'Haití Verde', subtitle: 'Preservemos nuestros espacios naturales', alt: 'Paisaje verde de Haití con montañas' },
    { id: 2, title: 'Sueños Familiares', subtitle: 'Construyendo un futuro mejor juntos', alt: 'Familia frente a su nueva casa moderna' },
    { id: 3, title: 'Orgullo Haitiano', subtitle: 'La Unión Hace La Fuerza', alt: 'Bandera haitiana ondeando con orgullo' },
    { id: 4, title: 'Educación y Formación', subtitle: 'Invirtiendo en la juventud haitiana', alt: 'Grupo de jóvenes estudiantes sonrientes' },
    { id: 5, title: 'Agricultura Sostenible', subtitle: 'Energía solar al servicio de nuestras tierras', alt: 'Granja solar con agricultores haitianos' }
  ],
  pt: [
    { id: 1, title: 'Haiti Verde', subtitle: 'Vamos preservar nossos espaços naturais', alt: 'Paisagem verde do Haiti com montanhas' },
    { id: 2, title: 'Sonhos de Família', subtitle: 'Construindo um futuro melhor juntos', alt: 'Família em frente à sua nova casa moderna' },
    { id: 3, title: 'Orgulho Haitiano', subtitle: 'A União Faz A Força', alt: 'Bandeira haitiana tremulando orgulhosamente' },
    { id: 4, title: 'Educação e Formação', subtitle: 'Investindo na juventude haitiana', alt: 'Grupo de jovens estudantes sorrindo' },
    { id: 5, title: 'Agricultura Sustentável', subtitle: 'Energia solar a serviço de nossas terras', alt: 'Fazenda solar com agricultores haitianos' }
  ]
};

const slideImages = [
  'https://images.unsplash.com/photo-1510796962426-959af1dc86e6?auto=format&fit=crop&q=80&w=1920',
  'https://images.pexels.com/photos/7579046/pexels-photo-7579046.jpeg?auto=compress&cs=tinysrgb&w=1920',
  'https://static.prod-images.emergentagent.com/jobs/19fa9495-8ba9-4369-985e-87df68b8ce3c/images/e573da66651365f4567037630d945ed036068568307df72c6ed4984ec9bfcda1.png',
  'https://images.unsplash.com/photo-1632215863153-0dae7657d0a9?auto=format&fit=crop&q=80&w=1920',
  'https://static.prod-images.emergentagent.com/jobs/19fa9495-8ba9-4369-985e-87df68b8ce3c/images/e9eaa06032f0b857de4ab63fc54f66912cd75283bb222cbfe26f67dbdff17406.png'
];

const HeroSlider = () => {
  const { i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const slides = (slidesData[i18n.language] || slidesData.fr).map((slide, index) => ({
    ...slide,
    image: slideImages[index]
  }));

  const slidesLength = slides.length;

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slidesLength);
  }, [slidesLength]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slidesLength) % slidesLength);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume autoplay after 5 seconds
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-slider">
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${slides[currentSlide].image})`,
            }}
          />
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
          
          {/* Slide caption */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="absolute bottom-32 left-6 md:left-12 text-white z-20"
          >
            <span className="bg-primary/80 px-4 py-1 rounded-full text-sm font-medium mb-2 inline-block">
              {slides[currentSlide].title}
            </span>
            <p className="text-lg md:text-xl text-white/90 mt-2 max-w-md">
              {slides[currentSlide].subtitle}
            </p>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 group"
        aria-label="Slide précédente"
        data-testid="slider-prev-btn"
      >
        <ChevronLeft className="w-6 h-6 group-hover:scale-110 transition-transform" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 group"
        aria-label="Slide suivante"
        data-testid="slider-next-btn"
      >
        <ChevronRight className="w-6 h-6 group-hover:scale-110 transition-transform" />
      </button>

      {/* Dots indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3" data-testid="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 ${
              index === currentSlide 
                ? 'w-10 h-3 bg-secondary rounded-full' 
                : 'w-3 h-3 bg-white/50 hover:bg-white/80 rounded-full'
            }`}
            aria-label={`Aller à la slide ${index + 1}`}
            data-testid={`slider-dot-${index}`}
          />
        ))}
      </div>

      {/* Progress bar for auto-play */}
      {isAutoPlaying && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-30">
          <motion.div
            key={currentSlide}
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 5, ease: 'linear' }}
            className="h-full bg-secondary"
          />
        </div>
      )}
    </div>
  );
};

export default HeroSlider;
